import { useCallback } from 'react';

import { Badge, Typography } from '@scaleaq/scaleui';
import { InventoryRepoPlus } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import {
  useWidgetSitePanelCtx,
  useWidgetSiteTabbedShellCtx,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useLayoutProps } from '@/hooks';
import { WidgetConfigModalProps } from '@/types.ts';
import { labelForDeviceType } from '@/utils';

import {
  CameraCardGrid,
  CameraPenCard,
  CameraPenHeader,
  CameraSelectCard,
  CameraSiteContent,
} from './cameraSelector.styles.ts';
import type { CameraWidgetConfig } from './cameraWidgetConfig.types.ts';

export const CameraSitePanel = () => {
  const { t } = useTranslation(['landingPage']);
  const { onSave, section } = useWidgetSitePanelCtx();
  const { shellWidgetId } = useWidgetSiteTabbedShellCtx();
  const { update, config } = useLayoutProps<CameraWidgetConfig>({
    widgetId: shellWidgetId,
    defaultConfig: { camera: { id: '', name: '' } },
  });

  const handleSelectCamera = useCallback(
    (camera: InventoryRepoPlus, siteScopedOnSave: WidgetConfigModalProps['onSave']) => {
      const nextConfig: CameraWidgetConfig = {
        camera: {
          id: camera.id,
          name: camera.name,
          parentId: camera.parentId,
          selectedStreamName: config.camera.selectedStreamName,
        },
        topBannerPinned: config.topBannerPinned,
        isCameraNameVisible: config.isCameraNameVisible,
      };
      update({ widgetId: shellWidgetId, config: nextConfig });
      siteScopedOnSave(nextConfig);
    },
    [
      config.camera.selectedStreamName,
      config.topBannerPinned,
      config.isCameraNameVisible,
      shellWidgetId,
      update,
    ]
  );

  const selectedCameraId = config.camera.id;

  return (
    <CameraSiteContent>
      {(section.groups ?? []).map(({ groupKey, groupLabel, items }) => (
        <CameraPenCard key={groupKey} $selected={items.some(item => item.id === selectedCameraId)}>
          <CameraPenHeader>
            <Typography type='heading_small' weight={600} color='inherit'>
              {groupLabel}
            </Typography>
          </CameraPenHeader>
          <CameraCardGrid>
            {items.map(camera => (
              <CameraSelectCard
                key={camera.id}
                type='button'
                $selected={selectedCameraId === camera.id}
                data-camera-select-card='true'
                data-camera-select-id={camera.id}
                onClick={() => handleSelectCamera(camera, onSave)}
                aria-label={t('landingPage:selectCameraNamed', { name: camera.name })}
              >
                <Typography type='heading_xsmall' weight={500} color='inherit'>
                  {camera.name}
                </Typography>
                <Badge type={'neutral'} label={labelForDeviceType(camera.type)} size={'large'} />
              </CameraSelectCard>
            ))}
          </CameraCardGrid>
        </CameraPenCard>
      ))}
    </CameraSiteContent>
  );
};
